/**
 * Quantity breaks – card selection, quantity sync, variant price/image updates.
 * Uses event delegation and defers init for faster load.
 */
(function() {
  const MAX_RETRIES = 3;

  function updateQuantity(quantity, variantId, retryCount = 0) {
    const qs = document.querySelector('quantity-selector-component');
    const input = qs?.refs?.quantityInput
      ? qs.refs.quantityInput
      : document.querySelector('input[name="quantity"]');
    if (input) {
      input.value = quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('blur', { bubbles: true }));
    } else if (retryCount < MAX_RETRIES) {
      setTimeout(() => updateQuantity(quantity, variantId, retryCount + 1), 100);
      return;
    }

    const variantInput = document.querySelector('input[name="id"][ref="variantId"]');
    if (variantInput && variantId && variantInput.value !== String(variantId)) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function formatPriceDisplay(cents, showCurrency = false, component = null) {
    if (typeof Theme !== 'undefined' && Theme.formatPriceSplitHTML) {
      return Theme.formatPriceSplitHTML(cents, showCurrency === true, 'quantity_breaks');
    }
    return formatMoney(cents, showCurrency, component);
  }

  function formatMoney(cents, showCurrency = false, component = null) {
    if (typeof window.Theme !== 'undefined' && typeof Theme.formatMoney === 'function') {
      let result = Theme.formatMoney(cents);
      if (showCurrency && Theme.currency && !result.includes(Theme.currency)) {
        result = `${result} ${Theme.currency}`.trim();
      }
      return result;
    }
    const amount = cents / 100;
    const currency = component?.dataset?.currency || (typeof window.Theme !== 'undefined' && window.Theme.currency) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function updateFreeGiftsState(component) {
    const freeGifts = component.querySelector('[data-free-gifts]');
    if (!freeGifts) return;
    const activeCard = component.querySelector('.quantity-breaks__card--active');
    const selectedIndex = activeCard ? parseInt(activeCard.dataset.optionIndex || '1', 10) : 1;
    freeGifts.querySelectorAll('[data-gift-card]').forEach(giftCard => {
      const unlockIndex = parseInt(giftCard.dataset.unlockIndex || '2', 10);
      const enabled = selectedIndex >= unlockIndex;
      giftCard.classList.toggle('quantity-breaks-gift-card--enabled', enabled);
      giftCard.classList.toggle('quantity-breaks-gift-card--locked', !enabled);
      const variantSelect = giftCard.querySelector('[data-variant-select]');
      if (variantSelect) {
        variantSelect.disabled = !enabled;
      }
      if (enabled) {
        const opt = variantSelect?.options[variantSelect?.selectedIndex];
        if (opt) {
          giftCard.dataset.variantId = opt.value;
        }
      }
    });
  }

  function updateGiftCardPriceBadge(giftCard, priceCents) {
    const qb = document.querySelector('[data-quantity-breaks-component]');
    const discountEnabled = giftCard.dataset.giftDiscountEnabled === 'true';
    let displayPrice = priceCents;
    let originalPrice = priceCents;
    if (discountEnabled) {
      const discountType = giftCard.dataset.giftDiscountType || 'percentage';
      const discountValue = parseFloat(giftCard.dataset.giftDiscountValue) || 0;
      const discountAmount = parseFloat(giftCard.dataset.giftDiscountAmount) || 0;
      if (discountType === 'percentage') {
        displayPrice = Math.round(priceCents * (100 - discountValue) / 100);
      } else {
        displayPrice = Math.max(0, priceCents - discountAmount);
      }
    }
    const badge = giftCard.querySelector('.quantity-breaks-gift-card__price-badge');
    if (!badge || !qb) return;
    const fmt = (c) => formatPriceDisplay(c, false, qb);
    if (discountEnabled && displayPrice < originalPrice) {
      badge.innerHTML = '<span class="quantity-breaks-gift-card__original-price">' + fmt(originalPrice) + '</span><span class="quantity-breaks-gift-card__discounted-price">' + fmt(displayPrice) + '</span>';
    } else {
      badge.innerHTML = fmt(originalPrice);
    }
  }

  function initFreeGiftsVariantSelects(component) {
    const freeGifts = component.querySelector('[data-free-gifts]');
    if (!freeGifts) return;
    freeGifts.querySelectorAll('[data-variant-select]').forEach(select => {
      select.addEventListener('change', function() {
        const giftCard = this.closest('[data-gift-card]');
        if (giftCard && this.options[this.selectedIndex]) {
          const opt = this.options[this.selectedIndex];
          giftCard.dataset.variantId = opt.value;
          const price = parseInt(opt.dataset?.price || opt.getAttribute('data-price') || '0', 10);
          if (price) updateGiftCardPriceBadge(giftCard, price);
        }
      });
    });
  }

  function getVariantDataFromPickers(pickersContainer) {
    const el = pickersContainer?.querySelector('.quantity-breaks__variant-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (_) {
      return null;
    }
  }

  function getRowSelectedOptionValues(row) {
    const selects = row.querySelectorAll('[data-variant-option-select]');
    return Array.from(selects)
      .sort((a, b) => parseInt(a.dataset.optionPosition || '0', 10) - parseInt(b.dataset.optionPosition || '0', 10))
      .map((sel) => sel.value);
  }

  function findVariantByOptions(variantData, selectedValues) {
    if (!variantData?.variants?.length || !selectedValues?.length) return null;
    return variantData.variants.find((v) => {
      if (v.option1 !== selectedValues[0]) return false;
      if (selectedValues.length > 1 && v.option2 !== selectedValues[1]) return false;
      if (selectedValues.length > 2 && v.option3 !== selectedValues[2]) return false;
      return true;
    }) || null;
  }

  function updateRowOptionAvailability(row, variantData) {
    const selects = Array.from(row.querySelectorAll('[data-variant-option-select]'))
      .sort((a, b) => parseInt(a.dataset.optionPosition || '0', 10) - parseInt(b.dataset.optionPosition || '0', 10));
    if (!selects.length || !variantData?.variants?.length) return;

    const selectedValues = selects.map((sel) => sel.value);
    selects.forEach((select, index) => {
      Array.from(select.options).forEach((opt) => {
        const testValues = selectedValues.slice();
        testValues[index] = opt.value;
        const match = findVariantByOptions(variantData, testValues);
        opt.disabled = !match || match.available === false;
      });
    });
  }

  function resolveRowVariant(row, pickersContainer) {
    const optionSelects = row.querySelectorAll('[data-variant-option-select]');
    if (!optionSelects.length) return null;

    const variantData = getVariantDataFromPickers(pickersContainer);
    if (!variantData) return null;

    updateRowOptionAvailability(row, variantData);
    let selectedValues = getRowSelectedOptionValues(row);
    let match = findVariantByOptions(variantData, selectedValues);

    if (!match || match.available === false) {
      match = variantData.variants.find((v) => v.available) || variantData.variants[0];
      if (match) {
        const selects = Array.from(optionSelects)
          .sort((a, b) => parseInt(a.dataset.optionPosition || '0', 10) - parseInt(b.dataset.optionPosition || '0', 10));
        selects.forEach((sel, idx) => {
          const val = idx === 0 ? match.option1 : idx === 1 ? match.option2 : match.option3;
          if (val && Array.from(sel.options).some((o) => o.value === val && !o.disabled)) {
            sel.value = val;
          }
        });
        selectedValues = getRowSelectedOptionValues(row);
        match = findVariantByOptions(variantData, selectedValues);
      }
    }

    if (match) {
      row.dataset.variantId = String(match.id);
    }
    return match;
  }

  // Presentation only: use the exact selected variant media, never a generic tier thumbnail.
  function updateBundleRowPhoto(row, match) {
    const image = row.querySelector('[data-sp-bundle-photo]');
    if (!image) return;
    const url = match?.image_url;
    image.hidden = !url;
    if (url && image.getAttribute('src') !== url) image.src = url;
    image.alt = [match?.option1, match?.option2, match?.option3].filter(Boolean).join(' / ');
  }

  function syncVariantPickersToCard(card) {
    if (!card?.dataset?.hasVariantPickers) return;
    const pickersContainer = card.querySelector('[data-quantity-breaks-variant-pickers]');
    if (!pickersContainer) return;

    const rows = pickersContainer.querySelectorAll('.quantity-breaks__variant-picker-row');
    const ids = [];

    rows.forEach((row) => {
      const optionSelects = row.querySelectorAll('[data-variant-option-select]');
      if (optionSelects.length) {
        const match = resolveRowVariant(row, pickersContainer);
        updateBundleRowPhoto(row, match);
        if (match?.id) ids.push(String(match.id));
        return;
      }

      const sel = row.querySelector('[data-variant-select]');
      if (sel?.options[sel.selectedIndex]) {
        ids.push(sel.options[sel.selectedIndex].value);
        const data = getVariantDataFromPickers(pickersContainer);
        updateBundleRowPhoto(row, data?.variants?.find(v => String(v.id) === sel.value));
      }
    });

    if (ids.length) card.dataset.variantIds = ids.join(',');
  }

  function updateRowVariantSwatch(row, variantMatch) {
    const swatchEl = row?.querySelector('[data-variant-swatch]');
    if (!swatchEl) return;
    const swatchSelect = swatchEl.closest('.quantity-breaks__variant-picker-select-wrapper')?.querySelector('select');
    const opt = swatchSelect?.options[swatchSelect?.selectedIndex];
    const bg = opt?.dataset?.swatchBackground || opt?.getAttribute('data-swatch-background');
    if (bg) {
      swatchEl.style.setProperty('--swatch-background', bg);
      swatchEl.style.display = '';
    } else if (variantMatch?.image_url) {
      swatchEl.style.setProperty('--swatch-background', `url(${variantMatch.image_url})`);
      swatchEl.style.display = '';
    } else {
      swatchEl.style.display = 'none';
    }
  }

  function updateVariantSwatch(select, variantMatch) {
    const row = select.closest('.quantity-breaks__variant-picker-row');
    if (row?.querySelector('[data-variant-option-select]')) {
      updateRowVariantSwatch(row, variantMatch);
      return;
    }

    const wrapper = select.closest('.quantity-breaks__variant-picker-select-wrapper');
    const swatchEl = wrapper?.querySelector('[data-variant-swatch]');
    if (!swatchEl) return;
    const opt = select?.options[select?.selectedIndex];
    const bg = opt?.dataset?.swatchBackground || opt?.getAttribute('data-swatch-background');
    if (bg) {
      swatchEl.style.setProperty('--swatch-background', bg);
      swatchEl.style.display = '';
    } else {
      swatchEl.style.display = 'none';
    }
  }

  function resizeVariantSelectToContent(select) {
    // Custom image chooser owns presentation; never measure its hidden native select.
    if (select.hidden || select.closest('[data-sp-bundle-row][data-sp-ready="true"]')) return;
    const opt = select?.options[select?.selectedIndex];
    if (!opt) return;
    const text = opt.textContent.replace(/\s*-\s*Unavailable$/i, '').trim() || ' ';
    const cs = getComputedStyle(select);
    const measure = document.createElement('span');
    measure.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;pointer-events:none;';
    measure.style.font = cs.font;
    measure.style.fontSize = cs.fontSize;
    measure.style.fontFamily = cs.fontFamily;
    measure.style.fontWeight = cs.fontWeight;
    measure.style.letterSpacing = cs.letterSpacing;
    measure.textContent = text;
    document.body.appendChild(measure);
    const textWidth = measure.offsetWidth;
    document.body.removeChild(measure);
    const padding = parseInt(cs.paddingLeft || 0, 10) + parseInt(cs.paddingRight || 0, 10);
    const arrowSpace = 28;
    select.style.width = Math.max(textWidth + padding + arrowSpace, 60) + 'px';
  }

  function buildVariantResource(match, variantId, price, compareAtPrice, imageUrl, mediaId) {
    const resolvedMediaId = mediaId || match?.media_id;
    const resolvedImageUrl = imageUrl || match?.image_url || null;
    const featuredMedia =
      resolvedMediaId || resolvedImageUrl
        ? {
            ...(resolvedMediaId ? { id: parseInt(resolvedMediaId, 10) } : {}),
            ...(resolvedImageUrl ? { preview_image: { src: resolvedImageUrl } } : {}),
          }
        : null;

    return {
      id: parseInt(variantId || match?.id, 10),
      price: price ?? match?.price,
      compare_at_price: compareAtPrice ?? (match?.compare_at_price || match?.price),
      featured_media: featuredMedia,
      featured_image: resolvedImageUrl,
    };
  }

  function getCardPrimaryVariantMatch(card) {
    const pickersContainer = card?.querySelector('[data-quantity-breaks-variant-pickers]');
    if (!pickersContainer) return null;

    const firstRow = pickersContainer.querySelector('.quantity-breaks__variant-picker-row');
    if (!firstRow) return null;

    const optionSelects = firstRow.querySelectorAll('[data-variant-option-select]');
    if (optionSelects.length) {
      return resolveRowVariant(firstRow, pickersContainer);
    }

    const sel = firstRow.querySelector('[data-variant-select]');
    const opt = sel?.options[sel?.selectedIndex];
    if (!opt) return null;

    return {
      id: opt.value,
      price: parseInt(opt.dataset?.price || opt.getAttribute('data-price') || '0', 10),
      compare_at_price: parseInt(opt.dataset?.compareAtPrice || opt.getAttribute('data-compare-at-price') || opt.dataset?.price || opt.getAttribute('data-price') || '0', 10),
      image_url: opt.dataset?.imageUrl || opt.getAttribute('data-image-url') || null,
      media_id: opt.dataset?.mediaId || opt.getAttribute('data-media-id') || null,
    };
  }

  let gallerySyncController = null;
  const SECTION_ID_PREFIX = 'shopify-section-';

  function normalizeSectionId(sectionId) {
    return sectionId?.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '') || '';
  }

  function getProductSectionId(gallery) {
    const section = gallery?.closest('.shopify-section');
    return section ? normalizeSectionId(section.id) : null;
  }

  function getProductVariantFetchUrl(variantId) {
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]')
      || document.querySelector('variant-picker');
    const gallery = document.querySelector('media-gallery');
    const url = new URL(window.location.href);

    if (variantPicker?.dataset?.productUrl) {
      url.pathname = new URL(variantPicker.dataset.productUrl, window.location.origin).pathname;
    } else {
      const root = (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      const handle = gallery?.dataset?.productHandle;

      if (handle) {
        url.pathname = `${base}products/${handle}`.replace(/\/{2,}/g, '/');
      } else {
        url.pathname = window.location.pathname.split('?')[0];
      }
    }

    url.searchParams.set('variant', String(variantId));

    const sectionId = getProductSectionId(gallery);
    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    return url.toString();
  }

  function preserveGalleryPresentation(existingGallery, newGallery) {
    if (!existingGallery || !newGallery) return true;

    const existingPresentation = existingGallery.dataset.presentation;
    if (!existingPresentation || existingPresentation === newGallery.dataset.presentation) {
      return true;
    }

    if (existingPresentation === 'grid' && !newGallery.querySelector('.media-gallery__grid')) {
      return false;
    }

    newGallery.dataset.presentation = existingPresentation;
    newGallery.classList.remove('media-gallery--grid', 'media-gallery--carousel');
    newGallery.classList.add(`media-gallery--${existingPresentation}`);

    if (existingPresentation === 'grid') {
      const gridModifierPattern = /^media-gallery--(one|two)-column$|^media-gallery--large-first-image$/;
      for (const className of existingGallery.classList) {
        if (gridModifierPattern.test(className)) {
          newGallery.classList.add(className);
        }
      }
    }

    return true;
  }

  function replaceMainProductGallery(html) {
    const gallery = document.querySelector('media-gallery');
    const newGallery = html.querySelector('media-gallery');
    if (!gallery || !newGallery || !gallery.isConnected) return;
    if (!preserveGalleryPresentation(gallery, newGallery)) return;

    gallery.replaceWith(newGallery);
  }

  function syncMainProductGallery(match, overrides = {}) {
    const variantId = overrides.variantId || match?.id;
    if (!variantId) return;

    const gallery = document.querySelector('media-gallery');
    const isGridPresentation = gallery?.dataset?.presentation === 'grid';

    if (!isGridPresentation) {
      const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]')
        || document.querySelector('variant-picker');

      if (variantPicker && typeof variantPicker.fetchUpdatedSection === 'function' && variantPicker.dataset.productUrl) {
        variantPicker.fetchUpdatedSection(getProductVariantFetchUrl(variantId));
        return;
      }
    }

    gallerySyncController?.abort();
    gallerySyncController = new AbortController();

    fetch(getProductVariantFetchUrl(variantId), {
      signal: gallerySyncController.signal,
      cache: 'no-store',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Gallery sync failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        replaceMainProductGallery(html);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('Quantity breaks: failed to sync product gallery', error);
        }
      });
  }

  // SP gallery intent belongs to the selected tier, NOT the last row's variant.
  // Keep the native price/cart event and bypass section fetches for this adapter.
  // A stable section ID survives native gallery AND whole-section replacement.
  let spGalleryIntent = null;
  let spGalleryObserver = null;
  function applySpGalleryIntent() {
    const intent = spGalleryIntent;
    if (!intent) return;
    const section = document.getElementById(intent.sectionId);
    const component = section?.querySelector('[data-quantity-breaks-component]');
    if (!component || component.dataset.spFirstMediaId !== intent.firstMediaId) return;
    if (component !== intent.component) {
      intent.component = component;
      initQuantityBreaks(component);
      if (intent.locked) {
        const card = component.querySelector(`.quantity-breaks__card[data-quantity="${intent.quantity}"]`);
        if (card) {
          const data = getVariantDataFromPickers(card.querySelector('[data-quantity-breaks-variant-pickers]'));
          card.querySelectorAll('[data-sp-bundle-row]').forEach((row, index) => {
            const variant = data?.variants.find(v => String(v.id) === intent.variantIds[index] && v.available !== false);
            if (!variant) return;
            row.querySelectorAll('select').forEach(select => {
              select.value = select.matches('[data-variant-option-select]') ? variant['option'+select.dataset.optionPosition] : String(variant.id);
            });
          });
          card.querySelector('[data-sp-tier-button]')?.click();
        }
      }
    }
    const gallery = section.querySelector('media-gallery');
    const slider = gallery?.slideshow || gallery?.querySelector('slideshow-component');
    if (!slider?.select) return;
    intent.gallery = gallery;intent.slider = slider;
    const activeId = slider.slides?.[slider.current]?.getAttribute('slide-id');
    if (intent.mediaId && activeId !== intent.mediaId) slider.select({id:intent.mediaId}, undefined, {animate:false});
  }
  function selectSpGalleryMedia(component, mediaId, firstHero = false) {
    gallerySyncController?.abort();
    const section = component.closest('.shopify-section');
    const card = component.querySelector('.quantity-breaks__card--active');
    const quantity = Number(card?.dataset.quantity || 1);
    const locked = quantity > 1;
    const firstMediaId = component.dataset.spFirstMediaId;
    if (!section?.id || !firstMediaId) return;
    spGalleryIntent = {sectionId:section.id,component,firstMediaId,quantity,locked,
      variantIds:(card?.dataset.variantIds || '').split(','),
      mediaId:String(firstHero ? firstMediaId : mediaId || firstMediaId)};
    if (!spGalleryObserver) {
      spGalleryObserver = new MutationObserver(() => {
        const intent = spGalleryIntent;
        if (!intent) return;
        const section = document.getElementById(intent.sectionId);
        const gallery = section?.querySelector('media-gallery');
        const slider = gallery?.slideshow || gallery?.querySelector('slideshow-component');
        const component = section?.querySelector('[data-quantity-breaks-component]');
        if (component !== intent.component || gallery !== intent.gallery || slider !== intent.slider) {
          applySpGalleryIntent();requestAnimationFrame(applySpGalleryIntent);
        }
      });
      spGalleryObserver.observe(document.body,{childList:true,subtree:true});
      const relinquish = e => {
        if (!spGalleryIntent) return;
        // Manual browsing relinquishes only media intent, never bundle/cart selection.
        if (e.target.closest('media-gallery,variant-picker')) spGalleryIntent.mediaId=null;
      };
      document.addEventListener('pointerdown',relinquish,true);
      document.addEventListener('keydown',relinquish,true);
    }
    applySpGalleryIntent();requestAnimationFrame(applySpGalleryIntent);
  }

  function dispatchVariantUpdateFromMatch(match, overrides = {}) {
    if (!match && !overrides.variantId) return;

    const resource = buildVariantResource(
      match,
      overrides.variantId || match?.id,
      overrides.price ?? match?.price,
      overrides.compareAtPrice ?? match?.compare_at_price,
      overrides.imageUrl ?? match?.image_url,
      overrides.mediaId ?? match?.media_id
    );

    const spComponent = document.querySelector('[data-quantity-breaks-component]:has([data-sp-bundle-row])');
    if (spComponent) {
      selectSpGalleryMedia(spComponent, resource.featured_media?.id, overrides.firstHero === true);
      // Prices/commerce still receive the genuine variant; gallery is handled without page scrolling.
      resource.featured_media = null;resource.featured_image = null;
    } else syncMainProductGallery(match, overrides);

    document.dispatchEvent(new CustomEvent('variant:update', {
      detail: { resource }
    }));
  }

  function handleVariantPickerChange(select) {
    const card = select.closest('.quantity-breaks__card');
    if (!card) return;

    const pickersContainer = select.closest('[data-quantity-breaks-variant-pickers]');
    const row = select.closest('.quantity-breaks__variant-picker-row');
    let variantId;
    let price;
    let compareAtPrice;
    let imageUrl;

    if (select.matches('[data-variant-option-select]') && row && pickersContainer) {
      const match = resolveRowVariant(row, pickersContainer);
      if (!match) return;
      variantId = String(match.id);
      price = match.price;
      compareAtPrice = match.compare_at_price || match.price;
      imageUrl = match.image_url || null;
      syncVariantPickersToCard(card);
      card.dataset.variantId = variantId;

      const isActive = card.classList.contains('quantity-breaks__card--active');
      if (isActive) {
        const quantity = parseInt(select.dataset.quantity || card.dataset.quantity || '2', 10);
        updateQuantity(quantity, variantId);
        dispatchVariantUpdateFromMatch(match);
      }

      resizeVariantSelectToContent(select);
      updateVariantSwatch(select, match);
      return;
    }

    if (!select.options[select.selectedIndex]) return;
    const opt = select.options[select.selectedIndex];
    variantId = opt.value;
    price = parseInt(opt.dataset?.price || opt.getAttribute('data-price') || '0', 10);
    compareAtPrice = parseInt(opt.dataset?.compareAtPrice || opt.getAttribute('data-compare-at-price') || price, 10);
    imageUrl = opt.dataset?.imageUrl || opt.getAttribute('data-image-url');
    const mediaId = opt.dataset?.mediaId || opt.getAttribute('data-media-id');

    syncVariantPickersToCard(card);
    card.dataset.variantId = variantId;

    const isActive = card.classList.contains('quantity-breaks__card--active');
    if (isActive) {
      const quantity = parseInt(select.dataset.quantity || card.dataset.quantity || '2', 10);
      updateQuantity(quantity, variantId);
      dispatchVariantUpdateFromMatch(null, {
        variantId,
        price,
        compareAtPrice,
        imageUrl,
        mediaId,
      });
    }

    resizeVariantSelectToContent(select);
    updateVariantSwatch(select);
  }

  function initQuantityBreaksVariantSelects(component) {
    component.querySelectorAll('[data-quantity-breaks-variant-pickers]').forEach((pickersContainer) => {
      pickersContainer.querySelectorAll('.quantity-breaks__variant-picker-row').forEach((row) => {
        if (row.querySelector('[data-variant-option-select]')) {
          resolveRowVariant(row, pickersContainer);
        }
      });
    });

    component.querySelectorAll('[data-quantity-breaks-variant-pickers] [data-variant-select], [data-quantity-breaks-variant-pickers] [data-variant-option-select]').forEach(select => {
      resizeVariantSelectToContent(select);
      if (select.matches('[data-variant-option-select]')) {
        const row = select.closest('.quantity-breaks__variant-picker-row');
        const pickersContainer = select.closest('[data-quantity-breaks-variant-pickers]');
        const match = row && pickersContainer ? resolveRowVariant(row, pickersContainer) : null;
        updateVariantSwatch(select, match);
      } else {
        updateVariantSwatch(select);
      }
      select.addEventListener('change', function() {
        handleVariantPickerChange(this);
      });
    });
  }

  function preselectRecommendedCard(component) {
    let recommended = component.querySelector('.quantity-breaks__card--recommended:not(.quantity-breaks__card--sold-out)');
    if (!recommended) {
      recommended = component.querySelector('.quantity-breaks__card:not(.quantity-breaks__card--sold-out)');
    }
    if (!recommended) return;
    syncVariantPickersToCard(recommended);
    const quantity = parseInt(recommended.dataset.quantity, 10);
    const variantId = recommended.dataset.variantId;
    updateQuantity(quantity, variantId);
    component.querySelectorAll('.quantity-breaks__card').forEach(c => {
      c.classList.remove('quantity-breaks__card--active');
    });
    recommended.classList.add('quantity-breaks__card--active');
    recommended.classList.remove('quantity-breaks__card--unselected');
    updateFreeGiftsState(component);
    recommended.querySelectorAll('[data-variant-select], [data-variant-option-select]').forEach(sel => {
      resizeVariantSelectToContent(sel);
      if (sel.matches('[data-variant-option-select]')) {
        const row = sel.closest('.quantity-breaks__variant-picker-row');
        const pickersContainer = sel.closest('[data-quantity-breaks-variant-pickers]');
        const match = row && pickersContainer ? resolveRowVariant(row, pickersContainer) : null;
        updateVariantSwatch(sel, match);
      } else {
        updateVariantSwatch(sel);
      }
    });
  }

  function getCardVariantTotal(card) {
    if (card?.dataset?.hasVariantPickers !== 'true') return null;
    const pickers = card.querySelector('[data-quantity-breaks-variant-pickers]');
    if (!pickers) return null;

    const variantData = getVariantDataFromPickers(pickers);
    const rows = pickers.querySelectorAll('.quantity-breaks__variant-picker-row');
    let totalPrice = 0;
    let totalCompare = 0;
    let count = 0;

    rows.forEach((row) => {
      if (row.querySelector('[data-variant-option-select]')) {
        const match = resolveRowVariant(row, pickers);
        if (match) {
          totalPrice += match.price || 0;
          totalCompare += match.compare_at_price || match.price || 0;
          count += 1;
        }
        return;
      }

      const sel = row.querySelector('[data-variant-select]');
      const opt = sel?.options[sel?.selectedIndex];
      if (opt) {
        totalPrice += parseInt(opt.dataset?.price || opt.getAttribute('data-price') || '0', 10);
        totalCompare += parseInt(opt.dataset?.compareAtPrice || opt.getAttribute('data-compare-at-price') || opt.dataset?.price || opt.getAttribute('data-price') || '0', 10);
        count += 1;
      }
    });

    return count > 0 ? { total: totalPrice, compare: totalCompare } : null;
  }

  function updateVariantPrices(event) {
    const component = document.querySelector('[data-quantity-breaks-component]');
    if (!component) return;
    const variant = event.detail?.resource;
    if (!variant?.price) return;

    const basePrice = variant.price;
    const baseCompareAtPrice = variant.compare_at_price || variant.price;
    const variantId = variant.id;
    const showCurrency = component.dataset.currencyCodeEnabled === 'true';
    const perUnitText = component.dataset.perUnitText || '/Pcs';
    const comparePriceType = component.dataset.comparePriceType || 'calculated';
    const cards = component.querySelectorAll('.quantity-breaks__card');

    cards.forEach(card => {
      const pickerTotal = getCardVariantTotal(card);
      if (pickerTotal) {
        syncVariantPickersToCard(card);
      }
      const firstRow = card.querySelector('.quantity-breaks__variant-picker-row');
      let cardVariantId = variantId;
      if (firstRow) {
        if (firstRow.dataset.variantId) {
          cardVariantId = firstRow.dataset.variantId;
        } else {
          const firstPickerSelect = firstRow.querySelector('[data-variant-select]');
          cardVariantId = firstPickerSelect?.options[firstPickerSelect?.selectedIndex]?.value || variantId;
        }
      }
      if (cardVariantId) card.dataset.variantId = cardVariantId;

      const quantity = parseInt(card.dataset.quantity, 10) || 1;
      const discountType = card.dataset.discountType || 'percentage';
      const discount = parseFloat(card.dataset.discount) || 0;
      // SP BOGO amounts are configured in the shop's AED currency.
      // Shopify converts fixed discounts at its currency rate, independently of
      // market-rounded catalog prices. Match the authoritative cart allocation.
      const bogoCurrencyRate = Number(window.Shopify?.currency?.rate || 1);
      const discountAmountFixed = Math.round((parseFloat(card.dataset.discountAmount) || 0) * bogoCurrencyRate);
      const showPerUnit = card.dataset.showPerUnit === 'true';

      let total;
      let baseCompareForCompare;
      if (pickerTotal) {
        total = pickerTotal.total;
        baseCompareForCompare = pickerTotal.compare;
      } else {
        total = basePrice * quantity;
        baseCompareForCompare = baseCompareAtPrice * quantity;
      }
      const nativeBogo = component.dataset.spBogoMode === 'native_bxgy';
      const discountAmount = nativeBogo ? basePrice * Math.floor(quantity / 2)
        : discountType === 'fixed' ? discountAmountFixed : total * discount / 100;
      const discountAmountFloored = nativeBogo ? discountAmount
        : discountType === 'fixed' ? discountAmountFixed : Math.floor(discountAmount);
      let finalPrice = nativeBogo ? basePrice * (quantity - Math.floor(quantity / 2))
        : total - discountAmountFloored;
      if (!nativeBogo && discountType !== 'fixed' && discountAmount % 1 > 0) {
        finalPrice += 1;
      }
      const effectiveDiscount = nativeBogo
        ? quantity > 0 ? Math.round(Math.floor(quantity / 2) * 100 / quantity) : 0
        : total > 0 ? Math.round((discountAmount / total) * 100) : 0;
      const discountAmountRounded = nativeBogo ? discountAmount
        : discountType === 'fixed' ? discountAmountFixed : Math.round(discountAmount);
      const comparePrice = comparePriceType === 'original' ? baseCompareForCompare : total;
      const finalPriceRounded = Math.round(finalPrice);
      const comparePriceRounded = Math.round(comparePrice);
      const shouldShowCompare = comparePriceType === 'original'
        ? effectiveDiscount > 0
          || (discountType === 'fixed' && discountAmountRounded > 0)
          || baseCompareForCompare > finalPriceRounded
        : effectiveDiscount > 0
          || (discountType === 'fixed' && discountAmountRounded > 0)
          || comparePriceRounded > finalPriceRounded;

      card.dataset.finalPriceCents = String(finalPriceRounded);
      card.dataset.comparePriceCents = String(comparePriceRounded);

      const priceContainer = card.querySelector('.quantity-breaks__price-container');
      if (!priceContainer) return;
      const finalFormatted = formatPriceDisplay(finalPriceRounded, showCurrency, component);
      const compareFormatted = formatPriceDisplay(comparePriceRounded, showCurrency, component);

      if (showPerUnit) {
        const perUnitPrice = Math.round(finalPriceRounded / quantity);
        let perUnitCompare = Math.round(comparePriceRounded / quantity);
        if (comparePriceType === 'original' && baseCompareForCompare > finalPriceRounded) {
          perUnitCompare = Math.round(baseCompareForCompare / quantity);
        }
        const priceEl = priceContainer.querySelector('.quantity-breaks__price');
        const compareEl = priceContainer.querySelector('.quantity-breaks__original-price');
        if (priceEl) {
          priceEl.innerHTML = formatPriceDisplay(perUnitPrice, showCurrency, component) + ' <span class="quantity-breaks__per-unit-text">' + perUnitText + '</span>';
        }
        if (shouldShowCompare) {
          const html = formatPriceDisplay(perUnitCompare, showCurrency, component) + ' <span class="quantity-breaks__per-unit-text">' + perUnitText + '</span>';
          if (compareEl) {
            compareEl.innerHTML = html;
            compareEl.style.display = '';
          } else if (priceEl) {
            const span = document.createElement('span');
            span.className = 'quantity-breaks__original-price';
            span.innerHTML = html;
            priceContainer.appendChild(span);
          }
        } else if (compareEl) compareEl.remove();
      } else {
        const priceEl = priceContainer.querySelector('.quantity-breaks__price');
        const compareEl = priceContainer.querySelector('.quantity-breaks__original-price');
        if (priceEl) priceEl.innerHTML = finalFormatted;
        if (shouldShowCompare) {
          if (compareEl) {
            compareEl.innerHTML = compareFormatted;
            compareEl.style.display = '';
          } else if (priceEl) {
            const span = document.createElement('span');
            span.className = 'quantity-breaks__original-price';
            span.innerHTML = compareFormatted;
            priceContainer.appendChild(span);
          }
        } else if (compareEl) compareEl.remove();
      }

      if (card.dataset.useOriginalCompareSavings === 'true') {
        const savingsBadge = card.querySelector('[data-original-savings-badge]');
        if (savingsBadge) {
          const savedAmount = Math.max(0, comparePriceRounded - finalPriceRounded);
          const savedPercent = comparePriceRounded > 0
            ? Math.round(savedAmount / comparePriceRounded * 100)
            : 0;
          const savingsTemplate = savingsBadge.dataset.savingsTemplate || 'Save [amount]';
          savingsBadge.innerHTML = savingsTemplate
            .replace(/\[%\]/g, savedPercent + '%')
            .replace(/\[amount\]/g, formatPriceDisplay(savedAmount, showCurrency, component));
          savingsBadge.hidden = savedAmount <= 0;
        }
      }
    });
  }

  function updateVariantImages(event) {
    const component = document.querySelector('[data-quantity-breaks-component]');
    if (!component) return;
    const variant = event.detail?.resource;
    if (!variant) return;

    let url = variant.featured_media?.preview_image?.src || variant.featured_image;
    if (!url && event.detail?.data?.html) {
      const el = event.detail.data.html.querySelector('[data-product-variant-media]');
      if (el) url = (el.getAttribute('data-product-variant-media') || '').split('&width=')[0].split('?width=')[0];
    }
    if (!url) return;

    const imageSize = parseInt(getComputedStyle(component).getPropertyValue('--image-size') || '60', 10) * 2;
    component.querySelectorAll('img[data-option-image]').forEach(img => {
      if (img.dataset.customImage === 'true') return;
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('width', String(imageSize));
        img.src = u.toString();
      } catch (_) {
        img.src = url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + imageSize;
      }
    });
  }

  function handleVariantUpdate(event) {
    updateVariantImages(event);
    updateVariantPrices(event);
  }

  // SP in-page chooser: presentation adapter; native selects retain all commerce ownership.
  let spChooserId = 0;
  function initSpBundleChoosers(component) {
    component.querySelectorAll('[data-sp-bundle-row]').forEach(row => {
      if (row.dataset.spReady) return;
      const container = row.closest('[data-quantity-breaks-variant-pickers]');
      const data = getVariantDataFromPickers(container);
      const selects = [...row.querySelectorAll('select')];
      if (!data?.variants?.length || !selects.length) return;
      const card = row.closest('.quantity-breaks__card');
      const photo = row.querySelector('[data-sp-bundle-photo]');
      const chooser = document.createElement('div');
      chooser.className = 'sp-bundle-chooser';
      const trigger = document.createElement('button');
      trigger.type = 'button'; trigger.className = 'sp-bundle-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox'); trigger.setAttribute('aria-expanded', 'false');
      const label = document.createElement('span'); label.className = 'sp-bundle-trigger-label';
      const caption = document.createElement('small');
      const colourOnly = /^(colou?r)$/i.test(container.dataset.spOptionNames || '');
      caption.textContent = document.documentElement.lang.startsWith('ar') ? (colourOnly ? 'اختر اللون' : 'اختر النوع') : (colourOnly ? 'Choose colour' : 'Choose variant');
      const title = document.createElement('strong');
      label.append(caption, title);
      const arrow = document.createElement('span');arrow.className='sp-bundle-chevron';arrow.textContent='';arrow.setAttribute('aria-hidden','true');
      if (photo) trigger.append(photo);
      trigger.append(label,arrow);
      const list = document.createElement('div');list.className='sp-bundle-list';list.hidden=true;list.inert=true;list.setAttribute('aria-hidden','true');
      list.id='sp-bundle-list-'+(++spChooserId);list.setAttribute('role','listbox');list.setAttribute('aria-label',selects[0].getAttribute('aria-label'));
      trigger.setAttribute('aria-controls',list.id);
      chooser.append(trigger,list);row.append(chooser);
      const names = v => [v.option1,v.option2,v.option3].filter(Boolean).join(' / ');
      const options = data.variants.map(v => {
        const option=document.createElement('button');option.type='button';option.className='sp-bundle-option';option.setAttribute('role','option');option.tabIndex=-1;option.dataset.variantId=String(v.id);
        // Missing combinations do not become invented options. Sold-out combinations stay visible and disabled.
        option.disabled=v.available===false;option.setAttribute('aria-disabled',String(option.disabled));
        if(v.image_url){const img=document.createElement('img');img.src=v.image_url;img.alt='';img.width=52;img.height=52;img.loading='eager';img.decoding='async';option.append(img);img.decode?.().catch(()=>{});}
        const text=document.createElement('span');text.textContent=names(v);option.append(text);
        const state=document.createElement('small');state.className='sp-bundle-option-state';option.append(state);
        option.addEventListener('click',event=>{event.stopPropagation();if(option.disabled)return;
          if(selectedId()===String(v.id)){close(true);return;}
          for(const select of selects){
            const val=select.matches('[data-variant-option-select]') ? v['option'+select.dataset.optionPosition] : String(v.id);
            select.value=val;
          }
          selects[0].dispatchEvent(new Event('change',{bubbles:true}));
          refresh();close(true);
        });
        list.append(option);return {v,option,state};
      });
      function selectedId(){const single=row.querySelector('[data-variant-select]');return single ? single.value : String(row.dataset.variantId);}
      let lastRenderedId;
      function refresh(){const id=selectedId();if(id===lastRenderedId)return;lastRenderedId=id;const v=data.variants.find(v=>String(v.id)===id);title.textContent=v?names(v):'';
        trigger.setAttribute('aria-label',caption.textContent+': '+title.textContent);
        options.forEach(({v,option,state})=>{const selected=String(v.id)===id;option.setAttribute('aria-selected',String(selected));option.tabIndex=selected&&!option.disabled?0:-1;state.textContent=option.disabled ? (document.documentElement.lang.startsWith('ar')?'غير متوفر':'Sold out') : selected?'✓':'';});
      }
      let motion;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      function setOpen(expanded, keyboard=false){
        if (trigger.getAttribute('aria-expanded') === String(expanded)) return;
        motion?.cancel();motion=null;
        trigger.setAttribute('aria-expanded',String(expanded));
        list.inert=!expanded;
        list.setAttribute('aria-hidden',String(!expanded));
        // Commit the layout once. Never animate height/margins or wait to accept the next tap.
        list.hidden=!expanded;
        if(expanded&&!reduced.matches){
          motion=list.animate([{opacity:0.5},{opacity:1}],{duration:90,easing:'ease-out'});
          const currentMotion=motion;motion.onfinish=()=>{if(motion===currentMotion)motion=null;};
        }
        if(expanded&&keyboard){const target=options.find(x=>x.option.getAttribute('aria-selected')==='true'&&!x.option.disabled)||options.find(x=>!x.option.disabled);target?.option.focus({preventScroll:true});}
      }
      function close(restore=false){if(trigger.getAttribute('aria-expanded')!=='true')return;if(restore&&trigger.isConnected)trigger.focus({preventScroll:true});setOpen(false);}
      function open(keyboard=false){component.querySelectorAll('.sp-bundle-trigger[aria-expanded="true"]').forEach(t=>{if(t!==trigger)t.click();});refresh();setOpen(true,keyboard);}
      trigger.addEventListener('click',e=>{e.stopPropagation();trigger.getAttribute('aria-expanded')==='true'?close(false):open(e.detail===0);});
      trigger.addEventListener('keydown',e=>{if(['ArrowDown','ArrowUp'].includes(e.key)){e.preventDefault();open(true);}});
      let search='',searchAt=0;
      chooser.addEventListener('keydown',e=>{
        if(e.key==='Escape'){e.preventDefault();e.stopPropagation();close(true);return;}
        if(list.inert)return;
        const enabled=options.filter(x=>!x.option.disabled).map(x=>x.option);let index=enabled.indexOf(document.activeElement);
        if(['ArrowDown','ArrowUp','Home','End'].includes(e.key)){e.preventDefault();index=e.key==='Home'?0:e.key==='End'?enabled.length-1:(index+(e.key==='ArrowDown'?1:-1)+enabled.length)%enabled.length;enabled[index]?.focus({preventScroll:true});}
        else if(e.key.length===1&&!e.ctrlKey&&!e.metaKey&&e.key!==' '){search=Date.now()-searchAt>700?'':search;search+=e.key.toLowerCase();searchAt=Date.now();enabled.find(o=>o.querySelector('span')?.textContent.toLowerCase().startsWith(search))?.focus({preventScroll:true});}
      });
      chooser.addEventListener('focusout',e=>{if(!chooser.contains(e.relatedTarget))close(false);});
      // Capturing on this component also sees clicks stopped by native picker wrappers.
      component.addEventListener('pointerdown',e=>{if(!chooser.contains(e.target))close(false);},true);
      const outside=e=>{if(!component.isConnected){document.removeEventListener('pointerdown',outside,true);return;}if(!component.contains(e.target))close(false);};
      document.addEventListener('pointerdown',outside,true);
      const observer=new MutationObserver(()=>{refresh();if(!card.classList.contains('quantity-breaks__card--active'))close(false);});
      observer.observe(card,{attributes:true,attributeFilter:['class','data-variant-ids']});
      const tierClose=e=>{if(e.target.closest('[data-sp-tier-button]'))close(false);};
      component.addEventListener('click',tierClose,true);
      const unload=e=>{if(e.target.contains(component)){observer.disconnect();document.removeEventListener('pointerdown',outside,true);document.removeEventListener('shopify:section:unload',unload);}};
      document.addEventListener('shopify:section:unload',unload);
      row.addEventListener('change',refresh);
      selects.forEach(s=>{s.hidden=true;s.tabIndex=-1;s.setAttribute('aria-hidden','true');});
      row.dataset.spReady='true';refresh();
    });
    component.querySelectorAll('[data-sp-tier-button]').forEach(button=>{
      const card=button.closest('.quantity-breaks__card');
      const sync=()=>button.setAttribute('aria-pressed',String(card.classList.contains('quantity-breaks__card--active')));
      if(!button.dataset.spReady){new MutationObserver(sync).observe(card,{attributes:true,attributeFilter:['class']});button.dataset.spReady='true';}sync();
    });
  }

  function initQuantityBreaks(componentEl) {
    const component = componentEl || document.querySelector('[data-quantity-breaks-component]');
    if (!component) return;

    if (!component.dataset.initialized) {
      component.dataset.initialized = '1';
      // Event delegation: one listener on container instead of per card
      component.addEventListener('click', function(e) {
        const card = e.target.closest('.quantity-breaks__card');
        if (!card || card.classList.contains('quantity-breaks__card--sold-out')) return;
        if (!card.classList.contains('quantity-breaks__card--recommended')) {
          component.dataset.userHasSelectedOther = 'true';
        }
        syncVariantPickersToCard(card);
        const quantity = parseInt(card.dataset.quantity, 10);
        const variantId = card.dataset.variantId;
        updateQuantity(quantity, variantId);
        component.querySelectorAll('.quantity-breaks__card').forEach(c => {
          c.classList.remove('quantity-breaks__card--active');
          if (c !== card && c.classList.contains('quantity-breaks__card--recommended')) {
            c.classList.add('quantity-breaks__card--unselected');
          }
        });
        card.classList.add('quantity-breaks__card--active');
        card.classList.remove('quantity-breaks__card--unselected');
        updateFreeGiftsState(component);
        const primaryVariant = getCardPrimaryVariantMatch(card);
        if (primaryVariant) {
          dispatchVariantUpdateFromMatch(primaryVariant, { firstHero: quantity > 1 });
        }
        card.querySelectorAll('[data-variant-select], [data-variant-option-select]').forEach(sel => {
          resizeVariantSelectToContent(sel);
          if (sel.matches('[data-variant-option-select]')) {
            const row = sel.closest('.quantity-breaks__variant-picker-row');
            const pickersContainer = sel.closest('[data-quantity-breaks-variant-pickers]');
            const match = row && pickersContainer ? resolveRowVariant(row, pickersContainer) : null;
            updateVariantSwatch(sel, match);
          } else {
            updateVariantSwatch(sel);
          }
        });
        const buy = document.querySelector('.buy-buttons-block');
        if (buy && !component.querySelector('[data-sp-bundle-row]')) setTimeout(() => buy.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
      });
    }

    // Reconcile initial server-rendered AED-configured fixed amounts with
    // Shopify's presentment currency before initializing the selected tier.
    updateVariantPrices({detail: {resource: {
      id: component.querySelector('.quantity-breaks__card')?.dataset.variantId,
      price: Number(component.dataset.spBogoBasePrice),
      compare_at_price: Number(component.dataset.spBogoComparePrice)
    }}});
    initFreeGiftsVariantSelects(component);
    initQuantityBreaksVariantSelects(component);
    initSpBundleChoosers(component);
    setTimeout(() => {
      if (!component.isConnected || (spGalleryIntent?.component === component && spGalleryIntent.locked)) return;
      preselectRecommendedCard(component);
      if (!component.querySelector('.quantity-breaks__card--recommended')) {
        updateFreeGiftsState(component);
      }
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initQuantityBreaks());
  } else {
    initQuantityBreaks();
  }

  document.addEventListener('variant:update', handleVariantUpdate);
  document.addEventListener('shopify:section:load', function(ev) {
    const comp = ev.target?.querySelector?.('[data-quantity-breaks-component]') || ev.detail?.querySelector?.('[data-quantity-breaks-component]');
    if (comp) {
      delete comp.dataset.initialized;
      initQuantityBreaks(comp);
    }
  });
})();
